import React, { useCallback, useMemo } from 'react';

import { getAnnotationUniqueId } from 'components/ChartTools/Annotations';

import {
  GGChart,
  GGMetric,
  GGSubMetric,
  GGAnnotationsMetric,
  GGSeries,
  GGDateAxis,
  GGMetricsTooltip,
  GGNoSeriesPlaceholder,
} from '@gogame/components';
import { useScreenDispatch, useScreenState } from '@gogame/screens';

import { CHANGE_CART_LOADING_TYPE } from 'scenes/Trackers/Tracker/reducers';

import { ChartActions } from './Actions';
import { AnnotationSidebar } from './Actions/Annotations/AnnotationSidebar';
import ChartAffix from './ChartAffix';
import NoDataPlaceholder from './NoDataPlaceholder';
import SettingsPreview from './SettingsPreview';
import { useMetricChart } from './hooks';
import {
  useChartState,
  useIsBreakdownMode,
  useColorGetter,
  useChartIndex,
  useCanLoadChart,
  useCanLoadBreakdownChart,
} from './hooks';
import { useCanLoadAnnotations } from './hooks';

const Chart = ({
  withPaper,
  tooltipPosStrategy,
  chartContainerStyles,
  chartSeries,
  isLoading,
  annotations,
  isAnnotationsLoading,
  metrics,
  isMetricsLoading,
  onChangeViewSettings,
  onExport,
  onExportGoogleDrive,
  onExportWhenReady,
  onOpenSubscriptionsForm,
}) => {
  const chartState = useChartState();
  const screenState = useScreenState();
  const canLoadChart = useCanLoadChart();
  const canLoadBreakdownChart = useCanLoadBreakdownChart();
  const isAnnotationsEnabled = useCanLoadAnnotations();
  const getMetricChart = useMetricChart();
  const isChartBreakdownMode = useIsBreakdownMode();
  const dispatch = useScreenDispatch();
  const chartIndex = useChartIndex();

  const lazy = chartState.lazy && (canLoadChart || canLoadBreakdownChart);

  const isCompareMode = useMemo(
    () => screenState.filtersCollection.length > 1,
    [screenState.filtersCollection]
  );
  const annotationUniqueId = useMemo(() => getAnnotationUniqueId(), []);

  const handleLoadData = useCallback(() => {
    dispatch({
      type: CHANGE_CART_LOADING_TYPE,
      payload: {
        index: chartIndex,
        value: false,
      },
    });
  }, []);

  const colorGetter = useColorGetter(chartSeries);

  const isDataLoading = isLoading || isAnnotationsLoading;

  return (
    <GGChart
      {...chartState.chartViewSettings}
      index={chartIndex}
      loading={isDataLoading}
      loaderType={'chart'}
      scale={chartState.settings.scale}
      scaleWeekStart={chartState.settings.scaleWeekStart}
      compareMode={isCompareMode}
      basis={annotations ? 'calc(100% - 16px)' : undefined}
      style={{ height: '100%', margin: 0, ...chartContainerStyles }}
      withPaper={withPaper}
      colorGetter={colorGetter}
      onLoadChart={handleLoadData}
      lazy={lazy}
      affix={
        <ChartAffix
          isChartBreakdownMode={isChartBreakdownMode}
          metrics={metrics}
          isMetricsLoading={isMetricsLoading}
        />
      }
      sidebar={
        <AnnotationSidebar
          items={annotations?.items}
          period={annotations?.period}
          id={annotationUniqueId}
        />
      }
      actions={
        <ChartActions
          annotationUniqueId={annotationUniqueId}
          metrics={metrics}
          chartSeries={chartSeries}
          onChangeViewSettings={onChangeViewSettings}
          disabledSettings={isLoading}
          disabledBreakdown={isLoading}
          onExport={onExport}
          onExportGoogleDrive={onExportGoogleDrive}
          onExportWhenReady={onExportWhenReady}
          onOpenSubscriptionsForm={onOpenSubscriptionsForm}
        />
      }
      settingsPreview={<SettingsPreview />}
    >
      {chartSeries?.map(({ period, rows, metrics, chartId }) => (
        <GGSeries
          data={rows}
          period={period}
          seriesKey={chartId}
          key={chartId}
          dateKey={'Date'}
        >
          {metrics.map((metric, j) => (
            <GGMetric
              key={metric.id}
              dataKey={metric.id}
              colorKey={metric.id}
              yAxisGroup={metric.yAxisGroup}
              valueType={metric.type}
              name={metric.name}
              compared={metric.compared}
              {...getMetricChart({
                id: metric.id,
                index: j,
              })}
            >
              {metric.trend && (
                <GGSubMetric
                  dataKey={metric.trend.id}
                  name={metric.trend.name}
                  mode={'TREND'}
                  type={'line'}
                />
              )}
              {metric.changeRate && (
                <GGSubMetric
                  dataKey={metric.changeRate.id}
                  name={metric.changeRate.name}
                  mode={'CHR'}
                  type={'line'}
                />
              )}
              {metric.changeRateDiff && (
                <GGSubMetric
                  dataKey={metric.changeRateDiff.id}
                  name={metric.changeRateDiff.name}
                  mode={'CHD'}
                  type={'line'}
                />
              )}
            </GGMetric>
          ))}
        </GGSeries>
      ))}
      {annotations && isAnnotationsEnabled && (
        <GGSeries
          data={annotations.rows}
          period={annotations.period}
          seriesKey={'an'}
        >
          <GGAnnotationsMetric dataKey={'annotations'} />
        </GGSeries>
      )}
      <GGMetricsTooltip posStrategy={tooltipPosStrategy} />
      <GGDateAxis template={'d. MMM'} />
      <GGNoSeriesPlaceholder text={<NoDataPlaceholder />} />
    </GGChart>
  );
};

export default Chart;
